import React, { useContext, useEffect, useState } from 'react'
import { Alert, Box, Grid, IconButton, Menu, MenuItem } from '@mui/material'
import LoadingContent from '@/Components/Elements/Loading/LoadingContent'
import UpperMenu from '@/feature/navigation/Topmenu'
import NewEnvironment from '../Components/NewEnvironment'
import EnvironmentCard from '../Components/EnvironmentCard'
import DefaultSearchbar from '@/Components/Elements/Search/DefaultSearchbar'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { faTools } from '@fortawesome/pro-duotone-svg-icons/faTools'
import { faCopy } from '@fortawesome/pro-duotone-svg-icons/faCopy'
import { faPlusCircle } from '@fortawesome/pro-duotone-svg-icons/faPlusCircle'
import { faTimes } from '@fortawesome/pro-duotone-svg-icons/faTimes'

import Pagination from '@/Components/Elements/Pagination'
import UpdateUserPassword from '../../../Components/Admin/Pages/User/UpdateUserPassword'
import { KeDoContext } from '../../../kedo-context'
import { useHistory } from 'react-router-dom'
import { useEnvironmentService } from '../../../context/EnvironmentServiceContext.tsx'
import { useSocketService } from '../../../context/SocketServiceContext.tsx'
import { StyledButton } from '@/Components/Elements/StyledComponents/Buttons'

const EnvironmentIndex = () => {
    const [page, setPage] = useState(0)
    const [pager, setPager] = useState(null)
    const [searchName, setSearchName] = useState('')

    const [fetching, setFetching] = useState(false)
    const [loading, setLoading] = useState(false)
    const [isNew, setIsNew] = useState(false)
    const [items, setItems] = useState([])

    const history = useHistory()
    const kedo = useContext(KeDoContext)
    const environmentService = useEnvironmentService()
    const socketService = useSocketService()

    const [anchorEl, setAnchorEl] = useState(null)
    const handleMenuOpen = (event) => setAnchorEl(event.currentTarget)
    const handleMenuClose = () => setAnchorEl(null)

    const clearPager = () => {
        setFetching(true)
        setPage(0)
        setSearchName('')
    }

    useEffect(() => {
        fetchEnvironments()
    }, [page])

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            setPage(0)
            fetchEnvironments()
        }, 300)

        return () => clearTimeout(delayDebounceFn)
    }, [searchName])

    const setEnvironment = async (item) => {
        setFetching(true)

        const environment = await environmentService.getEnvironment(item.id)
        kedo.env().setEnvironment(environment)
        history.push('/')

        socketService.environment.join(item.id)

        setFetching(false)

        window.scrollTo(0, 0)
    }

    const onChangePage = (pageNumber) => {
        setPage(pageNumber)
    }

    const fetchEnvironments = async () => {
        setFetching(true)

        try {
            const envListResult = await environmentService.getEnvironments({
                limit: 12,
                page: page,
                name: searchName,
            })
            setItems(envListResult.results)
            setPager(envListResult.pager)
            setFetching(false)

            return envListResult
        } catch (err) {
            //TODO: Handle errors here
            setFetching(false)
        }
    }

    /**
     * Checks if we need to redirect to another environment.
     *
     * @param items
     * @returns {boolean}
     */
    const redirectImmediately = (envItems) => {
        if (kedo.isAdminOrContractAdmin() || envItems.length > 1) {
            return false
        } else if (
            envItems.length === 1 &&
            localStorage.getItem('forceUpdate') === null
        ) {
            return true
        }
        return false
    }

    const resetButton = () => {
        return (
            <IconButton
                size="small"
                onClick={() => {
                    clearPager()
                }}
            >
                <FontAwesomeIcon icon={faTimes} />
            </IconButton>
        )
    }

    useEffect(() => {
        const doInit = async () => {
            const envListResult = await fetchEnvironments()
            if (redirectImmediately(envListResult.results)) {
                setEnvironment(envListResult.results[0])
                setLoading(true)
            }
        }
        doInit()
    }, [])

    if (localStorage.getItem('forceUpdate')) {
        return (
            <UpdateUserPassword
                kedo={kedo}
                onSuccess={() => {
                    localStorage.removeItem('forceUpdate')
                    fetchEnvironments()
                }}
            />
        )
    }

    return (
        <Box sx={{ padding: 0 }}>
            {isNew ? (
                <NewEnvironment
                    kedo={kedo}
                    onClose={() => {
                        setIsNew(false)
                        fetchEnvironments()
                    }}
                />
            ) : null}
            <UpperMenu kedo={kedo} template={'environment'} />
            <div className={'outerContainer fullHeight'}>
                <div className="mainContainer">
                    <Grid
                        container
                        spacing={2}
                        sx={{
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Grid size={{ xs: 12, md: 8, lg: 8 }}>
                            <h1>{kedo.t('Select environment')}</h1>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                            {kedo.isAdminOrContractAdmin() ? (
                                <div className={'environmentToolbar'}>
                                    <StyledButton
                                        title={kedo.t('Tools')}
                                        onClick={handleMenuOpen}
                                        variant="outlined"
                                        startIcon={
                                            <FontAwesomeIcon icon={faTools} />
                                        }
                                    >
                                        {kedo.t('Tools')}
                                    </StyledButton>

                                    <Menu
                                        anchorEl={anchorEl}
                                        open={Boolean(anchorEl)}
                                        onClose={handleMenuClose}
                                    >
                                        <MenuItem
                                            component="a"
                                            href="/templates"
                                            onClick={handleMenuClose}
                                        >
                                            <FontAwesomeIcon icon={faCopy} />
                                            &nbsp; {kedo.t('Templates')}
                                        </MenuItem>
                                    </Menu>

                                    <StyledButton
                                        onClick={() => setIsNew(true)}
                                        variant="contained"
                                        title={kedo.t('New')}
                                        sx={{ marginLeft: '15px' }}
                                        color="primary"
                                        startIcon={
                                            <FontAwesomeIcon
                                                icon={faPlusCircle}
                                            />
                                        }
                                    >
                                        {kedo.t('New')}
                                    </StyledButton>
                                </div>
                            ) : null}
                        </Grid>
                    </Grid>
                    <Box
                        sx={{
                            mb: '20px',
                        }}
                    >
                        <DefaultSearchbar
                            kedo={kedo}
                            handleChange={(e) => setSearchName(e.target.value)}
                            handleSearch={() => {
                                //
                            }}
                            searchName={searchName}
                            resetButton={resetButton}
                        />
                    </Box>
                    {fetching ? (
                        <LoadingContent kedo={kedo} />
                    ) : (
                        <Box>
                            {items.length <= 0 ? (
                                <Alert severity="info" variant="outlined">
                                    &nbsp; {kedo.t('No environments found')}
                                </Alert>
                            ) : null}
                            <Box
                                display="flex"
                                flexWrap="wrap"
                                gap={2}
                                justifyContent="flex-start"
                            >
                                {items.map((item) => (
                                    <Box key={item.id}>
                                        <EnvironmentCard
                                            kedo={kedo}
                                            item={item}
                                            environment={() => {
                                                setEnvironment(item)
                                            }}
                                            onClose={fetchEnvironments}
                                        />
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}
                    {pager && pager.pages && pager.pages > 0 ? (
                        <Pagination
                            onChangePage={onChangePage}
                            pager={pager}
                            limit={12}
                            searchName={searchName}
                            fetch={fetchEnvironments}
                        />
                    ) : null}
                </div>
            </div>
        </Box>
    )
}

export default EnvironmentIndex
